import Image from "components/common/images/Image";
import { CTaxType } from "enums";
import { ApplyItemDetailModel, CategoryModel, ContextModel, HideListItemModel, LimitedListItemModel } from "models";
import React, { useEffect, useState } from "react";
import XButton2 from "./common/button/XButton2";
import Chip from "./common/Chip";
import Tag from "./common/chip/Tag";
import FileInput from "./common/input/FileInput";
import RelativeItemDialog from "./dialog/RelativeItemDialog";
import TagDialog from "./dialog/TagDialog";
import ListSelectForm from "./exhibit/forms/ListSelectForm";
import SubImageForm from "./exhibit/forms/SubImageForm";
import ItemCategoryInputs from "./exhibit/ItemCategoryInputs";
import AppTooltip from "./common/AppTooltip";
import { TOOLTIP_DATA } from "enums/tooltip_data";
import CurrencyInput from "react-currency-input-field";
import { Utils } from "utils";

type PROPS = {
  ctx?: ContextModel;
  data: ApplyItemDetailModel;
  category: CategoryModel[];
  limitedLists: LimitedListItemModel[];
  hideLists: HideListItemModel[];
  otherItems: { item_cd: string; item_name: string }[];
  disabled: boolean;
  isImageNew?: boolean;
};

const ExhibitForms: React.FC<PROPS> = (props) => {
  const data = props.data;
  const disabled = props.disabled;

  const [itemName, setItemName] = React.useState("");
  const [janCode, setJanCode] = React.useState("");
  const [isIMEModing, setIsIMEModing] = useState(false);
  const [categories, setCategories] = React.useState<
    {
      apply_item_large_category_id: string;
      apply_item_medium_category_id: string;
      apply_item_small_category_id: string;
    }[]
  >([]);
  const [description, setDescription] = React.useState("");
  const [isDiscretionaryPrice, setIsDiscretionaryPrice] = React.useState(false);
  const [unitPrice, setUnitPrice] = React.useState("");
  const [ctaxType, setCTaxType] = React.useState<"" | number>("");
  const [tags, setTags] = React.useState<{ apply_item_tag_name: string }[]>([]);
  const [note, setNote] = React.useState("");
  // 関連商品 編集用データ
  const [relItemEditData, setRelItemEditData] =
    React.useState<{ item_cd: string; item_name: string }[]>();
  const [tagEditData, setTagEditData] =
    React.useState<{ tag_name: string }[]>();
  const onCompositionStart = () => {
    setIsIMEModing(true);
  };
  const onCompositionEnd = () => {
    setIsIMEModing(false);
  };
  const onKeyUp = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputElement = e.target as HTMLInputElement | HTMLTextAreaElement;
    const inputValue = inputElement.value;
  
    if (isIMEModing) {
      let numericValue = "";
  
      if (inputValue !== "") {
        const formattedValue = formatNumericInput(inputValue);
        const numericValueAsNumber = parseFloat(formattedValue.replace(/,/g, ""));
        data.apply_unit_price = numericValueAsNumber;
      } else {
        data.apply_unit_price = "";
      }
    } else {
      const numericValue = inputValue.replace(/[^\d.-]/g, "");
      const formattedNumericValue = formatNumericInput(numericValue);
      const numericValueAsNumber = parseFloat(numericValue.replace(/,/g, ""));
      data.apply_unit_price = numericValueAsNumber;
      setUnitPrice(formattedNumericValue);
    }
  };
  
  const formatNumericInput = (inputValue: any, thousandsSep = ","): string => {
    const sanitizedValue = inputValue
      .replace(/[^0-9.-]/g, "")
      .replace(/^(-)|-+/g, "$1")
      .replace(/^([^.]*\.)|\.+/g, "$1");
  
    const [integerPart, decimalPart = ""] = sanitizedValue.split(".");
    let formattedNumber = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  
    if (decimalPart.length > 0) {
      formattedNumber += `.${decimalPart.slice(0, 2)}`;
    }
  
    const hasDecimal = sanitizedValue.includes(".");
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const result = hasDecimal ? `${formattedIntegerPart}.${decimalPart.slice(0, 2)}` : formattedIntegerPart;
    if (/^\.[0-9]+$/.test(result)) {
      return `0${result}`;
    }
    return result
  };
  
  useEffect(() => {
    setItemName(data.apply_item_name);
    setJanCode(data.apply_jan_code);
    setCategories(data.apply_item_category);
    setDescription(data.apply_item_description);
    setIsDiscretionaryPrice(data.apply_is_discretionary_price);
    setUnitPrice(data.apply_unit_price.toString());
    setCTaxType(data.apply_ctax_type ?? "");
    setTags(data.apply_tag);
    setNote(data.apply_item_note);
  }, [data]);

  return (
    <div className="flex flex-col gap-4">
      {/* 商品名 */}
      <div className="flex items-center">
        <div className="w-3/12 flex justify-between">
          商品名<span className="ml-2 mr-auto text-red-500 font-bold">*</span>
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_name} />
        </div>
        <div className="flex-1">
          <input
            className="w-full"
            value={itemName}
            onChange={(e) => {
              const value = e.target.value;
              data.apply_item_name = value;
              setItemName(value);
            }}
            disabled={disabled}
          />
        </div>
      </div>
      {/* 商品画像 */}
      <div className="flex">
        <div className="w-3/12 mt-2 flex justify-between">
          商品メイン画像
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_main_image} />
        </div>
        <div className="flex-1">
          {disabled ? (
            <div className="flex">
              <Image data={data.apply_item_main_image} show_file_name />
            </div>
          ) : (
            <FileInput
              data={data.apply_item_main_image}
              hide_file_name
              preview
              show_preview_file_name
              ctx={props.ctx}
              name="商品メイン画像"
              maxSize={1}
              withNew={props.isImageNew}
            />
          )}
        </div>
      </div>
      <div className="flex items-start">
        <div className="w-3/12 flex justify-between">
          <div>
            商品サブ画像 <br />
            (最大10枚)
          </div>
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_sub_image} />
        </div>
        <div className="flex-1">
          {disabled ? (
            <div className="my-2 flex flex-wrap gap-2">
              {data.apply_item_sub_image.map((v, i) => {
                return (
                  <div key={i} className="max-w-[20%]">
                    <Image data={data.apply_item_sub_image[i]} show_file_name />
                  </div>
                );
              })}
            </div>
          ) : (
            <SubImageForm
              data={data.apply_item_sub_image}
              ctx={props.ctx}
              name="商品サブ画像"
              maxSize={1}
              withNew={props.isImageNew}
            />
          )}
        </div>
      </div>
      {/* JANコード */}
      <div className="flex items-center">
        <div className="w-3/12 flex justify-between">
          JANコード
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_jan_code} />
        </div>
        <div className="flex-1">
          <input
            className="w-full"
            value={janCode}
            onBlur={(e) => {
              const numericValue = (e.target as any).value.replace(
                /[^,\d]/g,
                ""
              );
              const validNumericValue = numericValue.match(/[\d,]+/g);
              if (validNumericValue) {
                const numericString = validNumericValue[0].replace(/,/g, "");
                data.apply_jan_code = numericString;
              } else {
                data.apply_jan_code = "";
              }
              setJanCode(data.apply_jan_code);
            }}
            onChange={(e) => {
              const value = e.target.value;
              data.apply_jan_code = value;
              setJanCode(value);
            }}
            disabled={disabled}
            type="tel"
          />
        </div>
      </div>
      {/* 商品カテゴリ */}
      <div className="flex items-start">
        <div className="w-3/12 mt-2 flex justify-between">
          商品カテゴリ
          <span className="ml-2 mr-auto text-red-500 font-bold">*</span>
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_category} />
        </div>
        <div className="flex-1 flex flex-col gap-2">
          {categories.map((v, i) => (
            <div key={i} className="flex items-center flex-row-reverse gap-2">
              <div
                className={`category-action ${
                  !disabled && data.apply_item_category.length > 1
                    ? ""
                    : "invisible"
                }`}
              >
                <XButton2
                  action={() => {
                    categories.splice(i, 1);
                    const tmp = [...categories];
                    data.apply_item_category = tmp;
                    setCategories(tmp);
                  }}
                />
              </div>
              <div className="flex-1 p-1 category-form border border-transparent">
                <ItemCategoryInputs
                  category={props.category}
                  data={v}
                  disabled={disabled}
                />
              </div>
            </div>
          ))}
          {!disabled && (
            <div className="mt-2 flex justify-start">
              <button
                className="group border border-jhk-blue rounded hover:bg-gray-100 disabled:bg-gray-300 disabled:border-gray-400"
                disabled={data.apply_item_category.length >= 5}
                onClick={() => {
                  const tmp = [...data.apply_item_category];
                  tmp.push({
                    apply_item_large_category_id: "",
                    apply_item_medium_category_id: "",
                    apply_item_small_category_id: "",
                  });
                  data.apply_item_category = tmp;
                  setCategories(tmp);
                }}
              >
                <div className="text-jhk-blue mx-3 my-1 group-disabled:text-gray-400">
                  +
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* 商品詳細文 */}
      <div className="flex">
        <div className="w-3/12 mt-2 flex justify-between">
          商品詳細文
          <span className="ml-2 mr-auto text-red-500 font-bold">*</span>
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_description} />
        </div>
        <div className="flex-1">
          <textarea
            className="w-full"
            value={description}
            onChange={(e) => {
              const value = e.target.value;
              data.apply_item_description = value;
              setDescription(value);
            }}
            disabled={disabled}
          />
        </div>
      </div>
      {/* 商品詳細添付(PDF) */}
      <div className="flex items-center">
        <div className="w-3/12 flex justify-between">
          商品詳細添付(PDF)
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_append_pdf} />
        </div>
        <div className="flex-1">
          {disabled ? (
            <div>{data.apply_item_append_pdf?.file_name}</div>
          ) : (
            <FileInput
              data={data.apply_item_append_pdf}
              accept="application/pdf"
              ctx={props.ctx}
              name="商品詳細添付(PDF)"
              maxSize={10}
              withNew={props.isImageNew}
            />
          )}
        </div>
      </div>
      {/* 公開範囲設定 */}
      <div className="flex items-center">
        <div className="w-3/12">
          公開範囲設定<span className="ml-2 text-red-500 font-bold">*</span>
        </div>
        <div className="flex-1">
          <ListSelectForm
            data={data}
            limitedLists={props.limitedLists}
            hideLists={props.hideLists}
            disabled={disabled}
          />
        </div>
      </div>
      {/* 個別見積 */}
      <div className="flex items-center">
        <div className="w-3/12 flex justify-between">
          個別見積
          <AppTooltip
            tooltip={TOOLTIP_DATA.ITEMS.apply_is_discretionary_price}
          />
        </div>
        <div className="flex-1">
          <div className="flex gap-8">
            <div>
              <input
                type="radio"
                id="radio2_1"
                name="radio2"
                checked={isDiscretionaryPrice === false}
                onChange={(e) => {
                  data.apply_is_discretionary_price = false;
                  setIsDiscretionaryPrice(false);
                }}
                disabled={disabled}
              />
              <label htmlFor="radio2_1" className="ml-2">
                なし
              </label>
            </div>
            <div>
              <input
                type="radio"
                id="radio2_2"
                name="radio2"
                checked={isDiscretionaryPrice === true}
                onChange={(e) => {
                  data.apply_is_discretionary_price = true;
                  setIsDiscretionaryPrice(true);
                }}
                disabled={disabled}
              />
              <label htmlFor="radio2_2" className="ml-2">
                あり
              </label>
            </div>
          </div>
        </div>
      </div>
      {/* 価格(税抜) */}
      <div className="flex items-center">
        <div className="w-3/12">
          価格(税抜)<span className="ml-2 text-red-500 font-bold">*</span>
        </div>
        <div className="flex-1">
          <input
            step={1}
            name="apply_unit_price"
            className="w-full"
            disabled={disabled || isDiscretionaryPrice}
            value={Utils.formatNumericInput(unitPrice)}
            type="tel"
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            onKeyUp={onKeyUp}
            onChange={(e) => {
              const inputElement = e.target as HTMLInputElement;
              let numericValue = inputElement.value;
              setUnitPrice(numericValue);
            }}
            onBlur={(e) => {
              const rawValue = (e.target as any).value;
              console.log("rawValue", rawValue);
              const sanitizedValue = formatNumericInput(rawValue);
              setUnitPrice(sanitizedValue);
            }}
          />
        </div>
      </div>
      {/* 課税区分 */}
      <div className="flex items-center">
        <div className="w-3/12">
          課税区分<span className="ml-2 text-red-500 font-bold">*</span>
        </div>
        <div className="flex-1">
          <select
            value={ctaxType}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                data.apply_ctax_type = null;
                setCTaxType(value);
              } else {
                const n = Number(value);
                data.apply_ctax_type = n;
                setCTaxType(n);
              }
            }}
            disabled={disabled}
          >
            <option value={""}>選択してください</option>
            <option value={CTaxType._1_KAZEI_HYOJUN}>課税（標準）</option>
            <option value={CTaxType._4_KAZEI_KEIGEN}>課税（軽減）</option>
            {/* <option value={CTaxType._5_KAZEI_KYOTSU}>課税（共通返還）</option> */}
            <option value={CTaxType._2_HI_KAZEI}>非課税</option>
            <option value={CTaxType._3_TAISHO_GAI}>対象外</option>
          </select>
        </div>
      </div>
      {/* 関連商品 */}
      <div className="flex items-start">
        <div className="w-3/12 pt-2 flex justify-between">
          関連商品
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_recommend_item} />
        </div>

        <div className="flex-1 mb-12">
          <button
            className="secondary px-4 py-2"
            onClick={() => {
              const tmp = data.apply_recommend_item.map((v) => {
                return {
                  item_cd: v.apply_recommend_item_cd,
                  item_name: v.apply_recommend_item_name,
                };
              });
              setRelItemEditData(tmp);
            }}
            disabled={disabled}
          >
            商品の編集
          </button>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.apply_recommend_item.map((item, index) => {
              return <Tag key={index} text={item.apply_recommend_item_name} />;
            })}
          </div>
        </div>
      </div>
      {/* タグ */}
      <div className="flex items-start">
        <div className="w-3/12 pt-2 flex justify-between">
          タグ
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_tag_name} />
        </div>
        <div className="flex-1 mb-12">
          <button
            className="secondary px-4 py-2"
            onClick={() => {
              const tmp = tags.map((v) => {
                return { tag_name: v.apply_item_tag_name };
              });
              setTagEditData(tmp);
            }}
            disabled={disabled}
          >
            タグの編集
          </button>
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((v, i) => {
              return (
                <Chip
                  key={i}
                  text={v.apply_item_tag_name}
                  color="#d1d5db"
                  textColor="#000000"
                />
              );
            })}
          </div>
        </div>
      </div>
      {/* 備考 */}
      <div className="flex items-start">
        <div className="w-3/12 flex justify-between">
          備考
          <AppTooltip tooltip={TOOLTIP_DATA.ITEMS.apply_item_note} />
        </div>
        <div className="flex-1">
          <textarea
            className="w-full"
            value={note}
            onChange={(e) => {
              const value = e.target.value;
              data.apply_item_note = value;
              setNote(value);
            }}
            disabled={disabled}
          />
        </div>
      </div>
      <RelativeItemDialog
        flg={!!relItemEditData}
        current={data.apply_item_cd!}
        list={props.otherItems}
        data={relItemEditData}
        close={(result) => {
          setRelItemEditData(undefined);
          data.apply_recommend_item = result.map((v) => {
            return {
              apply_recommend_item_cd: v.item_cd,
              apply_recommend_item_name: v.item_name,
            };
          });
          setTags(data.apply_tag);
        }}
      />
      <TagDialog
        flg={!!tagEditData}
        data={tagEditData}
        close={(result) => {
          setTagEditData(undefined);
          data.apply_tag = result.map((v) => {
            return { apply_item_tag_name: v.tag_name };
          });
          setTags(data.apply_tag);
        }}
      />
    </div>
  );
};

export default ExhibitForms;
